import { useState } from "react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { Download, Search } from "lucide-react";

const DOT = { green: "bg-emerald-500", amber: "bg-amber-400", gray: "bg-slate-300" };

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      year: "numeric", month: "long", day: "numeric",
    });
  } catch {
    return iso;
  }
}

export default function StatusPage() {
  const [id, setId] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const lookup = async () => {
    if (!id.trim() || !email.trim()) {
      toast.error("Please enter both your Submission ID and email address.");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const res = await api.post("/submissions/status", {
        public_id: id.trim(),
        email: email.trim(),
      });
      setResult(res.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No submission found.");
    } finally {
      setLoading(false);
    }
  };

  const certUrl = result
    ? `${API}/submissions/${encodeURIComponent(result.public_id)}/certificate?email=${encodeURIComponent(email.trim())}`
    : "";

  return (
    <div className="min-h-screen bg-white font-body text-ink">
      <Header />

      <section className="mx-auto max-w-7xl px-6 pb-12 pt-20 md:px-12 md:pt-28">
        <div className="text-xs uppercase tracking-[0.2em] text-calblue">Submission Status</div>
        <h1 className="mt-4 max-w-3xl font-serif text-5xl font-medium leading-[1.05] tracking-tight text-ink md:text-6xl">
          Check your submission status.
        </h1>
        <p className="mt-6 max-w-2xl text-lg leading-relaxed text-slate-700">
          Enter the Submission ID from your confirmation email along with the email address you
          used. For your privacy, both must match before any status is shown.
        </p>
      </section>

      <section className="mx-auto max-w-3xl px-6 pb-32 md:px-12">
        <div data-testid="status-lookup-form" className="border border-slate-200 bg-white p-6 md:p-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="text-sm font-medium text-ink">Submission ID</label>
              <Input
                data-testid="status-id-input"
                value={id}
                onChange={(e) => setId(e.target.value)}
                placeholder="CREP-2026-000001"
                className="mt-2 rounded-none border-slate-200 bg-white py-6 font-mono focus-visible:ring-cardinal"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-ink">Email address</label>
              <Input
                data-testid="status-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-2 rounded-none border-slate-200 bg-white py-6 focus-visible:ring-cardinal"
              />
            </div>
          </div>
          <Button
            data-testid="status-lookup-button"
            onClick={lookup}
            disabled={loading}
            className="mt-6 rounded-none bg-navy px-8 py-6 text-base font-medium text-white hover:bg-navy-hover"
          >
            <Search className="mr-2 h-4 w-4" />
            {loading ? "Checking…" : "Check Status"}
          </Button>
        </div>

        {result && (
          <div data-testid="status-result" className="mt-10 border border-slate-200 bg-white p-6 md:p-8">
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Submission ID</div>
                <div className="mt-1 font-mono text-lg text-navy">{result.public_id}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Status</div>
                <div className="mt-1 flex items-center gap-2" data-testid="status-label">
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${DOT[result.status_color] || DOT.gray}`} />
                  <span className="text-lg font-medium text-ink">{result.status_label}</span>
                </div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Submitted</div>
                <div className="mt-1 text-ink">{fmtDate(result.submitted_at)}</div>
              </div>
              <div>
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Current Stage</div>
                <div className="mt-1 text-ink">{result.stage}</div>
              </div>
              <div className="sm:col-span-2">
                <div className="text-xs uppercase tracking-[0.18em] text-slate-400">Estimated Timeline</div>
                <div className="mt-1 text-ink">{result.timeline}</div>
              </div>
            </div>

            <div className="mt-8 border-t border-slate-100 pt-6">
              <div className="text-xs uppercase tracking-[0.2em] text-calblue">Next Steps</div>
              <p className="mt-3 leading-relaxed text-slate-600">{result.next_steps}</p>
            </div>

            <div className="mt-8 border-t border-slate-100 pt-6">
              <div className="text-xs uppercase tracking-[0.2em] text-calblue">
                Your contribution has helped build
              </div>
              <div className="mt-4 grid grid-cols-3 gap-px bg-slate-200">
                {[
                  ["Verified Listings", result.stats.verified_listings],
                  ["Participant Experiences", result.stats.experiences],
                  ["California Counties Represented", result.stats.counties],
                ].map(([label, value]) => (
                  <div key={label} className="bg-white p-4 text-center">
                    <div className="font-serif text-3xl font-medium text-navy">{value}</div>
                    <div className="mt-1 text-xs leading-tight text-slate-500">{label}</div>
                  </div>
                ))}
              </div>
            </div>

            <a
              href={certUrl}
              target="_blank"
              rel="noreferrer"
              data-testid="status-certificate-button"
              className="mt-8 inline-flex items-center rounded-none border border-navy px-6 py-3 text-sm font-medium text-navy transition-colors hover:bg-navy hover:text-white"
            >
              <Download className="mr-2 h-4 w-4" /> Download Contribution Certificate
            </a>

            <p
              data-testid="status-disclaimer"
              className="mt-8 border-t border-slate-100 pt-6 text-xs leading-relaxed text-slate-500"
            >
              Submission status reflects only the research review process. It does not indicate
              whether any statement has been verified, endorsed, or accepted as fact. The
              California Rental Experience Project publishes only aggregated findings and does not
              publicly identify individual participants or specific properties.
            </p>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
}
